import { useState } from "react";
import { Button, Tag } from "@sortera/react-components";
import { LayoutGridIcon, PlusIcon } from "@sortera/react-icons";
import type { ProjectDto } from "../../types/index.ts";
import { StartPage, StartPageActions, StartPageLead } from "../../ui/start-page/index.ts";
import { CreateProjectDialog } from "../create-project/index.ts";
import { FACTORY_PIPELINES } from "../no-projects/index.ts";
import { ago, stageText } from "../project-header/index.ts";
import styles from "./project-grid.module.css";

/** The start page when no project is chosen: every project the person can reach, and a new one. */
export function ProjectGrid({
  projects,
  defaultEmail,
  onPick,
  onCreated,
}: {
  projects: ProjectDto[];
  defaultEmail: string;
  onPick: (repo: string) => void;
  /** Absent when making projects from here is switched off: then there is no button for it. */
  onCreated?: (repo: string) => void;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <StartPage
      title="Dina projekt"
      wide
      after={
        onCreated && (
          <CreateProjectDialog open={creating} onOpenChange={setCreating} defaultEmail={defaultEmail} onCreated={onCreated} />
        )
      }
    >
      {onCreated ? (
        <StartPageLead>
          Välj ett projekt att jobba i, eller skapa ett nytt. Sortera Factory sätter upp det på ungefär en minut.
        </StartPageLead>
      ) : (
        <StartPageLead>
          Välj ett projekt att jobba i. Nya skapas med{" "}
          <a href={FACTORY_PIPELINES} target="_blank" rel="noopener noreferrer">
            Sortera Factory i Azure DevOps
          </a>
          .
        </StartPageLead>
      )}

      {onCreated && (
        <StartPageActions>
          <Button color="secondary" size="small" icon={<PlusIcon />} onClick={() => setCreating(true)}>
            Skapa projekt
          </Button>
        </StartPageActions>
      )}

      <div className={styles.grid}>
        {projects.map((p) => (
          <button key={p.repo} type="button" className={styles.card} title={p.repo} onClick={() => onPick(p.repo)}>
            <span className={styles.icon} aria-hidden>
              <LayoutGridIcon size={18} />
            </span>
            <span className={styles.name}>{p.name}</span>
            {p.stage !== "unknown" && (
              <Tag size="sm" variant={p.stage === "mvp" ? "branded" : "default"} title={stageText(p.stage)} className={styles.stage}>
                {p.stage.toUpperCase()}
              </Tag>
            )}
            <span className={styles.used}>{p.lastUsedAt ? `Senast ${ago(p.lastUsedAt)}` : "Inte öppnat än"}</span>
          </button>
        ))}
      </div>
    </StartPage>
  );
}
